/**
 * CityContentPanel — pinned above the business grid on a city page.
 *
 * Three tabs: General News · Food News · Events.
 * Auto-loads via /api/city-content/:country/:state/:city?bucket=…
 * Anchored by a "Suggest a link/event" button that opens SuggestModal.
 */
import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Newspaper, UtensilsCrossed, CalendarDays, Plus, ExternalLink, X, Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const BUCKETS = [
  { key: "general", label: "General News", Icon: Newspaper },
  { key: "food",    label: "Food News",    Icon: UtensilsCrossed },
  { key: "events",  label: "Events",       Icon: CalendarDays },
];

function timeAgo(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d)) return "";
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  if (s < 30 * 86400) return `${Math.floor(s / 86400)}d ago`;
  return d.toLocaleDateString();
}

function SuggestModal({ country, state, city, bucket, onClose, onSubmitted }) {
  const [form, setForm] = useState({
    bucket: bucket || "general",
    title: "",
    description: "",
    url: "",
    photo_url: "",
    notes: "",
    submitter_name: "",
    submitter_email: "",
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Add a title first.");
    if (!form.url.trim() && !form.description.trim()) {
      return toast.error("Add a link or a description (or both).");
    }
    setBusy(true);
    try {
      const payload = { ...form };
      Object.keys(payload).forEach((k) => { if (!payload[k]) delete payload[k]; });
      await axios.post(`${API}/api/city-content/${country}/${state}/${encodeURIComponent(city)}/suggest`, payload);
      toast.success("Thanks — a moderator reviews within 24 hours.");
      onSubmitted?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Couldn't submit — retry?");
    } finally {
      setBusy(false);
    }
  };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div className="fixed inset-0 z-[110] bg-black/50 flex items-end sm:items-center justify-center p-3"
         onClick={onClose} data-testid="city-suggest-modal">
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()}
            className="bg-white w-full max-w-md rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-4 py-3 border-b flex items-center justify-between"
             style={{ borderColor: "#EADFCB", background: "#FDF6E3" }}>
          <div>
            <div className="text-[11px] font-mono uppercase tracking-wider" style={{ color: "#8B5E10" }}>
              Suggest for {city}, {state}
            </div>
            <div className="font-semibold" style={{ color: "#3E2A0A" }}>Share a link, photo or event</div>
          </div>
          <button type="button" onClick={onClose} className="opacity-60 hover:opacity-100"
                  data-testid="city-suggest-close"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-4 space-y-3 max-h-[70vh] overflow-y-auto">
          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Section</label>
            <div className="grid grid-cols-3 gap-2 mt-1">
              {BUCKETS.map(({ key, label, Icon }) => (
                <button key={key} type="button" onClick={() => set("bucket", key)}
                        className={`px-2 py-2 rounded-lg border text-xs font-semibold flex flex-col items-center gap-1 ${form.bucket === key ? "text-white" : ""}`}
                        style={form.bucket === key
                          ? { background: "#B25D38", borderColor: "#B25D38" }
                          : { borderColor: "#EADFCB", color: "#5A4A32" }}
                        data-testid={`city-suggest-bucket-${key}`}>
                  <Icon className="w-4 h-4"/>
                  {label.replace(" News", "")}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Title *</label>
            <input required maxLength={180} value={form.title}
                   onChange={(e) => set("title", e.target.value)}
                   placeholder="e.g. Summer Farmers Market opens Saturday"
                   className="w-full px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-title" />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Link (article, event page or map)</label>
            <input value={form.url}
                   onChange={(e) => set("url", e.target.value)}
                   placeholder="https://…"
                   className="w-full px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-url" />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Description</label>
            <textarea value={form.description} maxLength={1200} rows={3}
                      onChange={(e) => set("description", e.target.value)}
                      placeholder="What's happening? Who is it for? When?"
                      className="w-full px-3 py-2 rounded-lg border text-sm"
                      style={{ borderColor: "#EADFCB" }}
                      data-testid="city-suggest-description" />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Photo URL (optional)</label>
            <input value={form.photo_url}
                   onChange={(e) => set("photo_url", e.target.value)}
                   placeholder="https://…"
                   className="w-full px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-photo" />
          </div>

          <div>
            <label className="text-[11px] font-mono uppercase opacity-70">Notes for moderator (optional)</label>
            <input value={form.notes} maxLength={500}
                   onChange={(e) => set("notes", e.target.value)}
                   placeholder="Anything else we should know…"
                   className="w-full px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-notes" />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <input value={form.submitter_name} maxLength={120}
                   onChange={(e) => set("submitter_name", e.target.value)}
                   placeholder="Your name (optional)"
                   className="px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-name" />
            <input value={form.submitter_email} maxLength={200} type="email"
                   onChange={(e) => set("submitter_email", e.target.value)}
                   placeholder="Email (optional)"
                   className="px-3 py-2 rounded-lg border text-sm"
                   style={{ borderColor: "#EADFCB" }}
                   data-testid="city-suggest-email" />
          </div>

          <div className="pt-1 flex justify-end">
            <button disabled={busy}
                    className="px-4 py-2 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                    style={{ background: "#B25D38" }}
                    data-testid="city-suggest-submit">
              {busy ? "Sending…" : "Send suggestion"}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default function CityContentPanel({ country, state, city }) {
  const [bucket, setBucket] = useState("general");
  const [byBucket, setByBucket] = useState({ general: null, food: null, events: null });
  const [loading, setLoading] = useState(false);
  const [suggestOpen, setSuggestOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (byBucket[bucket]) return; // cached
      setLoading(true);
      try {
        const r = await axios.get(
          `${API}/api/city-content/${country}/${state}/${encodeURIComponent(city)}`,
          { params: { bucket, limit: 8 } });
        if (!cancelled) setByBucket((p) => ({ ...p, [bucket]: r.data }));
      } catch (_) {
        if (!cancelled) setByBucket((p) => ({ ...p, [bucket]: { items: [] } }));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [country, state, city, bucket, byBucket]);

  const items = byBucket[bucket]?.items || [];

  return (
    <section className="mb-8" data-testid="city-content-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div className="flex flex-wrap gap-2">
          {BUCKETS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setBucket(key)}
                    className={`inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border ${bucket === key ? "text-white" : ""}`}
                    style={bucket === key
                      ? { background: "#3C2A17", borderColor: "#3C2A17" }
                      : { borderColor: "#EADFCB", color: "#5A4A32", background: "#fff" }}
                    data-testid={`city-content-tab-${key}`}>
              <Icon className="w-3.5 h-3.5" />
              {label}
            </button>
          ))}
        </div>
        <button onClick={() => setSuggestOpen(true)}
                className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full border"
                style={{ borderColor: "#B25D38", color: "#B25D38", background: "#fff" }}
                data-testid="city-content-suggest">
          <Plus className="w-3.5 h-3.5"/> Suggest a link / event
        </button>
      </div>

      {loading && !items.length && (
        <div className="flex items-center gap-2 text-xs py-6 justify-center" style={{ color: "#8B5E10" }}>
          <Loader2 className="w-4 h-4 animate-spin"/> Pulling latest {bucket} coverage for {city}…
        </div>
      )}

      {!loading && items.length === 0 && (
        <div className="rounded-xl border p-6 text-center text-sm"
             style={{ borderColor: "#EADFCB", background: "#FDF6E3", color: "#5A4A32" }}>
          No {bucket} stories yet. <button onClick={() => setSuggestOpen(true)}
                                            className="underline font-semibold"
                                            style={{ color: "#B25D38" }}>
            Suggest one →
          </button>
        </div>
      )}

      {items.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {items.map((it) => (
            <a key={it.id} href={it.url} target="_blank" rel="noreferrer"
               className="group rounded-xl border p-3 hover:shadow-md transition bg-white flex flex-col gap-1.5"
               style={{ borderColor: "#EADFCB" }}
               data-testid="city-content-item">
              {it.photo_url && (
                <img src={it.photo_url} alt=""
                     className="w-full aspect-video object-cover rounded-lg mb-1" />
              )}
              <div className="text-[10px] font-mono uppercase tracking-wider flex items-center gap-1.5"
                   style={{ color: "#8B5E10" }}>
                {it.source_kind === "user" ? (
                  <span className="px-1.5 py-0.5 rounded" style={{ background: "#FBEED6" }}>Community</span>
                ) : (
                  <span>{it.source || "News"}</span>
                )}
                <span>·</span>
                <span>{timeAgo(it.published_at)}</span>
              </div>
              <h4 className="text-sm font-semibold leading-tight group-hover:underline"
                  style={{ color: "#3C2A17" }}>
                {it.title}
              </h4>
              {it.summary && (
                <p className="text-xs opacity-80 line-clamp-3" style={{ color: "#5A4A32" }}>
                  {it.summary}
                </p>
              )}
              <div className="text-[10px] flex items-center gap-1 mt-auto pt-1 opacity-60"
                   style={{ color: "#8B5E10" }}>
                <ExternalLink className="w-3 h-3"/> {(it.url || "").replace(/^https?:\/\//, "").split("/")[0]}
              </div>
            </a>
          ))}
        </div>
      )}

      {suggestOpen && (
        <SuggestModal country={country} state={state} city={city} bucket={bucket}
                      onClose={() => setSuggestOpen(false)}
                      onSubmitted={() => setByBucket({ general: null, food: null, events: null })} />
      )}
    </section>
  );
}
